import { COMMON_TEXT, WORDS } from '@/constants';
import { useFindSimulationScorecard, useResponsive, useSimulationRouting } from '@/hooks';
import { useGlobalStore } from '@/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatDate,
  formatScorecardPrintDate,
  getNextSimulation,
  getScoreCardSubtitle,
} from '@/utils';
import type { IGlobalStateSidebarScorecard } from '@/store/types';

/** Props for the useScorecardModalFeature hook. */
interface UseScorecardModalFeatureProps {
  /** The context in which the sidebar is being used, either 'Sales Representative' or 'Manager'. */
  context: 'Sales Representative' | 'Manager';
  /** Optional callback function to be called when the sidebar is closed. */
  onClose?: () => void;
}

type ScorecardTransitionPhase = 'idle' | 'exiting' | 'entering';

/** Return values for the useScorecardModalFeature hook. */
interface ScorecardModalFeatureReturn {
  certification: IGlobalStateSidebarScorecard['certification'];
  dateCompleted: string | undefined;
  handleClose: () => void;
  handleGetNextScoreCard: () => void;
  handleRetakeRolePlayClick: () => void;
  handleViewCertificateClick: () => void;
  isOpen: boolean;
  isPrinting: boolean;
  isXlUp: boolean;
  nextScoreCardExists: boolean;
  nextSimulation: ReturnType<typeof getNextSimulation>;
  printDate: string;
  printRef: React.RefObject<HTMLDivElement>;
  printTitle: string;
  score: IGlobalStateSidebarScorecard['score'];
  scorecardTransitionPhase: ScorecardTransitionPhase;
  simulation: IGlobalStateSidebarScorecard['simulation'];
  simulationSubtitleText: string | null;
  simulationTitleText: string;
  userName: IGlobalStateSidebarScorecard['userName'];
}

const SCORECARD_EXIT_DURATION_MS = 180;
const SCORECARD_ENTER_FRAME_MS = 24;

/**
 * Custom hook to manage the state and behavior of the scorecard modal.
 *
 * This hook provides all necessary values and functions to control the
 * scorecard modal, including opening, closing, printing, and navigating
 * between scorecards.
 */
export const useScorecardModalFeature = ({
  context,
  onClose,
}: UseScorecardModalFeatureProps): ScorecardModalFeatureReturn => {
  // ============================================================
  // State, Refs, and Hooks
  // ============================================================
  const { navigateToSimulation } = useSimulationRouting();
  const isXlUp = useResponsive({ breakpoint: 'xl', direction: 'min' });
  const printRef = useRef<HTMLDivElement>(null);
  const transitionTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const [scorecardTransitionPhase, setScorecardTransitionPhase] =
    useState<ScorecardTransitionPhase>('idle');

  // ============================================================
  // Global store values and actions
  // ============================================================
  const {
    isPrinting,
    resetActives,
    scores,
    setSidebarCertificate,
    setSidebarScorecard,
    sidebarScorecard,
  } = useGlobalStore();

  useEffect(() => {
    return () => {
      transitionTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      transitionTimeoutsRef.current = [];
    };
  }, []);

  // ============================================================
  // Memoized values and computations
  // ============================================================

  /**
   * Memoized values for certification, score, and simulation based on the
   * current sidebar scorecard context.
   *
   * This ensures that the component only re-computes these values when the
   * relevant parts of the sidebar scorecard context change.
   *
   * It also provides a clear structure for accessing these values throughout the component.
   */
  const [certification, score, simulation, userName] = useMemo(() => {
    return [
      sidebarScorecard?.certification || null,
      sidebarScorecard?.score || null,
      sidebarScorecard?.simulation || null,
      sidebarScorecard?.userName || null,
    ];
  }, [sidebarScorecard]);

  // Ensure modal score carries runtime cutoffs even when the sidebar payload
  // comes from a sparse source (e.g. legacy history entries).
  const normalizedScore = useMemo(() => {
    if (!score) return null;

    const matchingScore = scores.find((candidate) => {
      if (score.sessionId && candidate.sessionId === score.sessionId) {
        return true;
      }

      if (score.uuid && candidate.uuid === score.uuid) {
        return true;
      }

      if (score.id && candidate.id === score.id) {
        return true;
      }

      return false;
    });

    if (matchingScore?.runtimeRatingCutoffs) {
      return {
        ...score,
        runtimeRatingCutoffs: matchingScore.runtimeRatingCutoffs,
      };
    }

    const simulationId = score.simulationId || simulation?.id?.toString();

    if (!simulationId) return score;

    const sameSimulationScore = scores.find((candidate) => {
      return candidate.simulationId === simulationId;
    });

    if (!sameSimulationScore?.runtimeRatingCutoffs) {
      return score;
    }

    return {
      ...score,
      runtimeRatingCutoffs: sameSimulationScore.runtimeRatingCutoffs,
    };
  }, [score, scores, simulation?.id]);

  /**
   * Memoized value to determine if the sidebar is open based on the active property
   * of the sidebar scorecard context. This allows the component to efficiently determine
   * when to render the sidebar without unnecessary re-renders when other parts of the context change.
   */
  const isOpen = useMemo(() => sidebarScorecard?.active || false, [sidebarScorecard]);

  /**
   * Formats the date completed for display in the title. If the score has a dateCompleted
   * value, it formats it using the formatDate utility to a readable format.
   * If not, it returns an empty string.
   */
  const dateCompleted = normalizedScore?.dateCompleted
    ? formatDate(normalizedScore.dateCompleted, 'fullDatetime')
    : undefined;

  /**
   * Memoized title for the print document, combining simulation title and
   * user name if available. Falls back to a default title if necessary
   * data is missing. This ensures that the printed document has a meaningful
   * title based on the scorecard context.
   */
  const printTitle = useMemo(() => {
    if (!sidebarScorecard) return COMMON_TEXT.SCORECARD;

    const name = userName || '';
    const simulationTitle = simulation?.title || '';
    const certificationTitle = certification?.title || '';

    return `${certificationTitle}: ${simulationTitle} - ${name}`;
  }, [sidebarScorecard, userName, simulation, certification]);

  /**
   * Determine the formatted date string for printing. This formats the score's dateCompleted
   * into a more human-readable format that includes both the date and time.
   */
  const printDate = useMemo(() => {
    return formatScorecardPrintDate(normalizedScore?.dateCompleted);
  }, [normalizedScore?.dateCompleted]);

  /**
   * Determine the page title based on the simulation title and printing state. When printing,
   * we want to include the user's name in the title for better context, especially if they are
   * sharing the PDF with others (e.g., manager). When not printing, we want to show the simulation
   * title along with "Scorecard" for clarity. We use useMemo here to avoid unnecessary recalculations
   * of the title on every render, since it only depends on a few props and state values.
   */
  const simulationTitleText = useMemo(() => {
    const simulationTitle = simulation?.title || '';
    if (simulationTitle) return `${simulationTitle} ${WORDS.SCORECARD}`;
    return COMMON_TEXT.SCORECARD;
  }, [simulation]);

  /**
   * Determine the subtitle text for the scorecard based on the simulation type,certification title,
   * and score pass/fail status. This provides additional context about the scorecard, such as whether
   * it was a practice or final simulation, and whether the user passed or failed. We use useMemo
   * to optimize performance by only recalculating this value when the relevant dependencies change.
   */
  const simulationSubtitleText = useMemo(() => {
    return getScoreCardSubtitle(
      simulation?.type ?? 'role-play',
      certification?.title,
      normalizedScore?.pass,
    );
  }, [simulation?.type, certification?.title, normalizedScore?.pass]);

  /**
   * Finds the next scorecard in the simulation history.
   */
  const findNextScoreCard = useFindSimulationScorecard({
    direction: 'previous',
    score: normalizedScore,
    scores,
    simulation,
  });

  /**
   * Finds the next simulation for the sidebar using the getNextSimulation utility function.
   */
  const nextSimulation = useMemo(
    () => getNextSimulation({ simulation, certification }),
    [simulation, certification],
  );

  /**
   * Determines if there is a next scorecard available in the sidebar.
   */
  const nextScoreCardExists = useMemo(() => {
    // Eject early if the button should not be shown based on props or context
    if (context === 'Manager') return false;

    // Eject if necessary data is missing
    if (!scores || !simulation || !simulation.history || !normalizedScore) {
      return false;
    }

    // Check if the history exists and has more than one score
    if (simulation.history.length <= 1) return false;

    // Check if the next scorecard exists
    if (findNextScoreCard === null) return false;

    // Return true if a next scorecard exists
    return true;
  }, [context, simulation, normalizedScore, scores, findNextScoreCard]);

  // ============================================================
  // Handlers and other functions
  // ============================================================

  /**
   * Handles "Retake role-play" button click
   */
  const handleRetakeRolePlayClick = () => {
    if (!certification || !simulation) return;
    return navigateToSimulation(certification, simulation);
  };

  /**
   * Handles loading the next scorecard in the sidebar.
   */
  const handleGetNextScoreCard = () => {
    // Eject if necessary data is missing
    if (!sidebarScorecard) return;
    if (!sidebarScorecard?.certification) return;
    if (!sidebarScorecard?.score) return;
    if (!sidebarScorecard?.simulation) return;
    if (!sidebarScorecard?.simulation?.history) return;
    if (!findNextScoreCard) return;
    if (scorecardTransitionPhase !== 'idle') return;

    setScorecardTransitionPhase('exiting');

    const exitTimeoutId = setTimeout(() => {
      setSidebarScorecard({
        active: true,
        certification: certification,
        score: findNextScoreCard,
        simulation: simulation,
        title: null,
        userName: userName,
      });
      setScorecardTransitionPhase('entering');

      const enterTimeoutId = setTimeout(() => {
        setScorecardTransitionPhase('idle');
        transitionTimeoutsRef.current = [];
      }, SCORECARD_ENTER_FRAME_MS);

      transitionTimeoutsRef.current = [enterTimeoutId];
    }, SCORECARD_EXIT_DURATION_MS);

    transitionTimeoutsRef.current = [exitTimeoutId];
  };

  /**
   * Handles closing the sidebar by resetting active selections and
   * clearing the sidebar scorecard context.
   */
  const handleClose = () => {
    transitionTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    transitionTimeoutsRef.current = [];
    setScorecardTransitionPhase('idle');
    resetActives();
    setSidebarScorecard(null);
    if (onClose) onClose();
  };

  /**
   * Handles viewing the certificate associated with the scorecard by setting the
   * sidebar certificate context. This allows users to easily navigate from their scorecard
   * to the certificate they earned for that simulation.
   */
  const handleViewCertificateClick = () => {
    // Eject if necessary data is missing
    if (!certification || !userName) return;

    // Clear scorecard sidebar
    setSidebarScorecard(null);

    // Set certificate sidebar with the relevant certification and user information
    setSidebarCertificate({
      active: true,
      certification,
      score: normalizedScore || null,
      simulation: simulation || null,
      title: null,
      userName,
    });
  };

  // ============================================================
  // Return values for the feature contract
  // ============================================================
  return {
    certification,
    dateCompleted,
    handleClose,
    handleGetNextScoreCard,
    handleRetakeRolePlayClick,
    handleViewCertificateClick,
    isOpen,
    isPrinting,
    isXlUp,
    nextScoreCardExists,
    nextSimulation,
    printDate,
    printRef,
    printTitle,
    score: normalizedScore,
    scorecardTransitionPhase,
    simulation,
    simulationSubtitleText,
    simulationTitleText,
    userName,
  };
};
